import React, { useEffect, useRef } from 'react';
import { Map as MapIcon, Navigation, ExternalLink, Tag, Maximize } from 'lucide-react';
import L from 'leaflet';
import { createMarkerClusterGroup } from '../../../lib/mapCluster';
import { createBaseTileLayer } from '../../../lib/mapTiles';
import 'leaflet/dist/leaflet.css';
import 'leaflet.markercluster/dist/MarkerCluster.css';
import 'leaflet.markercluster/dist/MarkerCluster.Default.css';
import 'leaflet.markercluster';
import { StoreInfo } from '../../../../types';
import { clusterStores, getClusterTooltipHtml } from '../../../utils/mapUtils';
import { useTranslation } from 'react-i18next';
import { sanitizeStoreCoordinates } from '../../../../utils/storeUtils';
import { chartColorAt, useChartCurrency } from './chartTheme';
import { mapTooltipOptions, enableSmartTooltipPlacement } from '../../../lib/mapTooltip';
import { createClusterIcon, createPinIcon, PIN_TOOLTIP_OFFSET } from '../../../lib/mapMarkers';

interface MerchantMapCardProps {
  stores: (StoreInfo & { totalCost?: number; date?: string })[];
  title?: string;
  currencySymbol?: string;
}

export const MerchantMapCard: React.FC<{ props: MerchantMapCardProps; isStreaming?: boolean }> = ({ 
  props,
  isStreaming
}) => {
  const { t } = useTranslation();
  
  // AI might pass { stores: [...] } or just the array [...] as props
  const rawStores = (props as any)?.stores || (Array.isArray(props) ? props : []);
  const stores = Array.isArray(rawStores) ? rawStores : [];
  const title = (props as any)?.title || t('chat.merchants_nearby');
  const activeCurrencySymbol = useChartCurrency(props as any);

  // Only plottable stores; (0, 0) placeholders would drop pins in the Atlantic.
  const plottable = React.useMemo(
    () => stores
      .map(s => ({ ...s, coordinates: sanitizeStoreCoordinates(s?.coordinates) }))
      .filter(s => s.coordinates),
    [stores],
  );
  const maxSpend = React.useMemo(
    () => Math.max(0, ...plottable.map(s => (typeof s.totalCost === 'number' ? s.totalCost : 0))),
    [plottable],
  );
  const showsSpend = maxSpend > 0;
  
  const mapContainerRef = useRef<HTMLDivElement>(null);
  const mapRef = useRef<L.Map | null>(null);
  const [mapReady, setMapReady] = React.useState(false);
  const mapId = React.useMemo(() => `map-${Math.random().toString(36).substr(2, 9)}`, []);

  useEffect(() => {
    if (!mapContainerRef.current || mapRef.current) return;

    // Initialize map with a safe default view
    const map = L.map(mapContainerRef.current, { 
      zoomControl: false,
      fadeAnimation: false // Disable animations to prevent sync issues during re-renders
    }).setView([0, 0], 2);

    createBaseTileLayer('light').addTo(map);

    enableSmartTooltipPlacement(map);
    
    mapRef.current = map;
    setMapReady(true);

    // Force resize handling
    setTimeout(() => {
      if (mapRef.current) mapRef.current.invalidateSize();
    }, 300);

    return () => {
      if (mapRef.current) {
        mapRef.current.remove();
        mapRef.current = null;
        setMapReady(false);
      }
    };
  }, []);

  useEffect(() => {
    if (!mapRef.current || !mapReady || plottable.length === 0) return;

    const map = mapRef.current;
    
    // Clear existing layers
    map.eachLayer(layer => {
      if (layer instanceof L.Marker || layer instanceof L.FeatureGroup) {
        map.removeLayer(layer);
      }
    });

    // Spending maps color pins by spend rank, not raw share: one big store would
    // otherwise leave every other pin at the same end of the gradient.
    const spendRanks = [...new Set(plottable.map(s => (typeof s.totalCost === 'number' ? s.totalCost : 0)))].sort((a, b) => a - b);
    const rankColor = (spend: number) => {
      const below = spendRanks.filter(v => v <= spend).length - 1;
      return chartColorAt(spendRanks.length > 1 ? below / (spendRanks.length - 1) : 1);
    };

    const clusterGroup = createMarkerClusterGroup(showsSpend ? {
      iconCreateFunction: (cluster: any) => {
        const children = cluster.getAllChildMarkers();
        const clusterSpend = children.reduce((sum: number, m: any) => sum + (m.options.spend || 0), 0);
        return createClusterIcon(cluster.getChildCount(), chartColorAt(Math.min(1, clusterSpend / Math.max(maxSpend, clusterSpend))));
      },
    } : {}).addTo(map);

    const clusters = clusterStores(plottable as StoreInfo[]);

    Object.values(clusters).forEach(clusterStores => {
      const s = clusterStores[0];
      if (!s.coordinates) return;

      const lat = Number(s.coordinates.lat);
      const lng = Number(s.coordinates.lng);

            // STRICT VALIDATION: Ensure both lat and lng are valid numbers
            if (!isNaN(lat) && !isNaN(lng)) {
              const spend = clusterStores.reduce((sum, c: any) => sum + (typeof c.totalCost === 'number' ? c.totalCost : 0), 0);
              // Same pin as every other map; on spending maps its color follows the spend gradient.
              const marker = L.marker([lat, lng], {
                icon: showsSpend ? createPinIcon({ color: rankColor(spend) }) : createPinIcon(),
                spend,
              } as L.MarkerOptions)
                .addTo(clusterGroup)
                .bindTooltip(
                  getClusterTooltipHtml(clusterStores, s, showsSpend
                    ? { stats: [{ label: t('common.total'), value: `${activeCurrencySymbol}${spend.toFixed(2)}`, tone: 'emerald' }] }
                    : {}),
                  mapTooltipOptions({ offset: PIN_TOOLTIP_OFFSET }),
                );
              
              marker.on('click', () => {
                if (mapRef.current) {
                   mapRef.current.setView([lat, lng], 16, { animate: true, duration: 1 });
                }
              });
            }    });
    
    const bounds = clusterGroup.getBounds();
    if (bounds.isValid()) {
      // Only fit bounds if we have valid markers
      try {
        map.fitBounds(bounds.pad(0.3), { animate: false });
      } catch (e) {
        console.error("Map fitBounds error:", e);
      }
    }
  }, [plottable, mapReady, showsSpend, maxSpend, activeCurrencySymbol]);

  if (stores.length === 0 && !isStreaming) {
    return (
      <div className="bg-white rounded-2xl p-6 border border-gray-100 shadow-sm text-center">
        <p className="text-xs font-black text-gray-400 uppercase tracking-widest">{t('chat.no_merchants_found')}</p>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-[2.5rem] border border-gray-100 shadow-xl overflow-hidden animate-in fade-in slide-in-from-bottom-2 duration-300">
      <div className="p-6 bg-[#1a2b3c] flex justify-between items-center text-white">
        <div className="flex items-center gap-3">
          <div className="p-2 bg-emerald-500 rounded-xl shadow-lg">
            <MapIcon size={18} />
          </div>
          <div className="min-w-0">
            <div className="text-[10px] font-black uppercase tracking-widest text-emerald-400 leading-none mb-1">{t('chat.merchant_discovery')}</div>
            <p className="text-sm font-black text-white leading-tight">{title}</p>
          </div>
        </div>
      </div>

      <div className="relative h-[240px] w-full bg-gray-50 border-y border-gray-100">
        <div id={mapId} ref={mapContainerRef} className="absolute inset-0 z-0 h-full w-full rounded-none" />
        
        {plottable.length > 0 && (
          <button
            onClick={(e) => {
               e.preventDefault();
               if (mapRef.current) {
                  const bounds = L.latLngBounds(plottable.map(s => [s.coordinates!.lat, s.coordinates!.lng] as [number, number]));
                  if (bounds.isValid()) {
                    mapRef.current.fitBounds(bounds.pad(0.3), { animate: true, duration: 1 });
                  }
               }
            }}
            className="absolute bottom-4 right-4 z-20 bg-white text-[#1a2b3c] p-2 rounded-xl shadow-lg border border-gray-100 hover:bg-emerald-50 hover:text-emerald-500 transition-colors active:scale-90" 
            title={t('history.reset_view')}
          >
            <Maximize size={16} />
          </button>
        )}

        {isStreaming && (
          <div className="absolute inset-0 z-10 flex items-center justify-center bg-white/40 backdrop-blur-[1px] animate-pulse">
             <p className="text-[10px] font-black text-emerald-600 uppercase tracking-widest">{t('chat.plotting_locations')}...</p>
          </div>
        )}
      </div>

      <div className="p-4 max-h-[160px] overflow-y-auto bg-white divide-y divide-gray-50">
        {stores.map((s, i) => (
          <div key={i} className="py-3 flex justify-between items-center group">
            <div className="min-w-0 flex-1 pr-4">
              <p className="text-[12px] font-bold text-[#1a2b3c] truncate leading-tight mb-0.5">{s.name}</p>
              <div className="flex flex-col gap-0.5">
                {s.address && (
                  <p className="text-[9px] font-black text-gray-400 uppercase truncate tracking-tighter">
                    {s.address}
                  </p>
                )}
                {typeof s.totalCost === 'number' && (
                  <p className="text-[10px] font-black text-emerald-600 uppercase tracking-tighter">
                    {t('chat.total_spent_at', { amount: `${activeCurrencySymbol}${s.totalCost.toFixed(2)}` })}
                  </p>
                )}
              </div>
            </div>
            <div className="flex gap-2 shrink-0">
              <a 
                href={s.url || `https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(s.name + ' ' + (s.address || ''))}`}
                target="_blank"
                rel="noopener noreferrer"
                className="p-2 bg-emerald-50 text-emerald-600 rounded-lg hover:bg-emerald-100 transition-colors flex items-center justify-center"
                title={t('common.navigate')}
              >
                <Navigation size={12} />
              </a>
            </div>
          </div>
        ))}
      </div>

      <div className="px-6 py-4 bg-gray-50/50 flex justify-center border-t border-gray-100">
          <div className="flex items-center gap-1 text-[9px] font-black text-gray-400 uppercase tracking-widest">
            {t('chat.locations_mapped', { count: stores.length })} <ExternalLink size={10} />
          </div>
      </div>
    </div>
  );
};
